'use client';

import { useEffect, useState } from 'react';
import {
  isChunkLoadError,
  autoReloadOnce,
  hasAutoReloadedThisSession,
  getBuildId,
  getNetworkInfo,
} from '@/lib/chunk-error-handler';

/**
 * Next.js 全局错误页面
 *
 * 此文件会捕获根 layout 中的错误，是最后一道防线
 * 注意：
 * 1. 必须是 'use client' 组件
 * 2. 必须包含自己的 <html> 和 <body> 标签
 * 3. 只在生产环境激活
 *
 * 行为：
 * - ChunkLoadError: 自动重载一次（静默），失败则显示刷新按钮
 * - 其他错误: 记录错误，显示友好提示，提供返回首页按钮
 *
 * 参考：https://nextjs.org/docs/app/building-your-application/routing/error-handling#handling-errors-in-root-layouts
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [isChunkError, setIsChunkError] = useState(false);
  const [shouldShowUI, setShouldShowUI] = useState(false);

  useEffect(() => {
    // 记录错误到控制台
    console.error('Global error caught by global-error.tsx:', error);

    const isChunk = isChunkLoadError(error);
    setIsChunkError(isChunk);

    if (isChunk) {
      // Handle ChunkLoadError
      const alreadyReloaded = hasAutoReloadedThisSession();
      const willReload = !alreadyReloaded;

      // Report to PostHog with chunk-specific info
      try {
        if (typeof window !== 'undefined' && (window as any).posthog) {
          (window as any).posthog.capture('global_error_boundary_triggered', {
            error_type: 'chunk_load_error',
            error_message: error.message,
            error_digest: error.digest,
            error_stack: error.stack,
            pathname: window.location.pathname,
            href: window.location.href,
            build_id: getBuildId(),
            user_agent: navigator.userAgent,
            did_auto_reload: willReload,
            already_reloaded: alreadyReloaded,
            ...getNetworkInfo(),
          });
        }
      } catch (e) {
        console.error('Failed to report chunk error to PostHog:', e);
      }

      if (willReload) {
        // Auto-reload once (this will navigate away)
        console.log('[ChunkLoadError] Auto-reloading once to recover...');
        autoReloadOnce();
        // Don't show UI - we're reloading
      } else {
        // Already reloaded, show fallback UI
        console.log('[ChunkLoadError] Already reloaded once, showing fallback UI');
        setShouldShowUI(true);
      }
    } else {
      // Non-chunk error - report and show UI immediately
      try {
        if (typeof window !== 'undefined' && (window as any).posthog) {
          (window as any).posthog.capture('global_error_boundary_triggered', {
            error_type: 'general_error',
            error_message: error.message,
            error_digest: error.digest,
            error_stack: error.stack,
            pathname: window.location.pathname,
            user_agent: navigator.userAgent,
          });
        }
      } catch (e) {
        console.error('Failed to report global error to PostHog:', e);
      }
      setShouldShowUI(true);
    }
  }, [error]);

  // Don't render UI while auto-reload is happening
  if (!shouldShowUI) {
    return null;
  }

  const handleAction = () => {
    if (typeof window !== 'undefined') {
      if (isChunkError) {
        // For chunk errors: hard reload
        window.location.reload();
      } else {
        // For other errors: go home
        window.location.href = '/quest';
      }
    }
  };

  const actionText = isChunkError ? 'Refresh' : 'Go Home';
  const message = isChunkError
    ? 'Failed to load some resources. Please refresh the page.'
    : "Don't worry, let's go back to the home page";

  return (
    <html lang="en">
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Error - Pengi</title>
        <style>{`
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
              'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
              sans-serif;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            background-color: #F3F4F6;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            padding: 1rem;
          }
          .error-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2rem;
          }
          .error-content {
            text-align: center;
          }
          .error-title {
            font-size: 1.875rem;
            font-weight: 700;
            color: #1F2937;
            margin-bottom: 1rem;
          }
          .error-message {
            font-size: 1.125rem;
            color: #6B7280;
          }
          .button-wrapper {
            width: 12rem;
            background-color: #58a700;
            border-radius: 9999px;
            padding-bottom: 3px;
            margin-bottom: 3px;
            cursor: pointer;
          }
          .error-button {
            font-weight: 700;
            padding: 0.375rem 1.5rem;
            border-radius: 9999px;
            color: white;
            background-color: #42C52D;
            border: none;
            font-size: 1.25rem;
            cursor: pointer;
            width: 100%;
            transition: all 0.1s;
          }
          .error-button:hover {
            filter: brightness(1.1);
          }
          .error-button:active {
            transform: translateY(3px);
          }
        `}</style>
      </head>
      <body>
        <div className="error-container">
          <div className="error-content">
            <h1 className="error-title">Something went wrong</h1>
            <p className="error-message">{message}</p>
          </div>
          <div className="button-wrapper">
            <button onClick={handleAction} className="error-button">
              {actionText}
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
